import { useState, useEffect, useRef } from 'react';
import { sipClient } from '../services/sip';
import { wsClient } from '../services/websocket';

interface User {
  id: string;
  name?: { String: string; Valid: boolean } | string;
  sip_username?: { String: string; Valid: boolean } | string;
  email?: { String: string; Valid: boolean } | string;
}

function extractStr(v: { String: string; Valid: boolean } | string | undefined): string {
  if (!v) return '';
  if (typeof v === 'string') return v;
  return v.Valid ? v.String : '';
}

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string) || 'http://localhost:8080/api/v1';

export function TestCradle() {
  const [users, setUsers] = useState<User[]>([]);
  const [sipUsername, setSipUsername] = useState('');
  const [fromNumber, setFromNumber] = useState('+15550001234');
  const [fromName, setFromName] = useState('Test Caller');
  const [record, setRecord] = useState(true);
  const [loading, setLoading] = useState(false);
  const [activeCallUUID, setActiveCallUUID] = useState<string | null>(null);
  const [status, setStatus] = useState<{ ok: boolean; message: string } | null>(null);
  const [recordingURL, setRecordingURL] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    fetch(`${API_BASE}/users`)
      .then((r) => r.json())
      .then((json) => {
        const list: User[] = Array.isArray(json) ? json : (json.data ?? []);
        setUsers(list);
        if (list.length > 0) setSipUsername(extractStr(list[0].sip_username));
      })
      .catch(() => setStatus({ ok: false, message: 'Failed to load users' }));
  }, []);

  // Listen for WebSocket call_ended / call_updated events so the UI updates immediately
  // when the extension hangs up (rather than waiting for the next polling tick).
  useEffect(() => {
    if (!activeCallUUID) return;

    const unsubscribe = wsClient.addMessageHandler((msg) => {
      const data = msg.data ?? {};
      const msgUUID: string = data.call_uuid ?? '';
      if (msgUUID !== activeCallUUID) return;

      if (msg.type === 'call_ended') {
        const recUrl: string =
          (typeof data.recording_url === 'string' && data.recording_url) || '';
        if (recUrl) setRecordingURL(recUrl);
        setStatus({ ok: true, message: `Call ended` });
        setActiveCallUUID(null);
      } else if (msg.type === 'call_updated') {
        const callStatus: string =
          typeof data.status === 'string' ? data.status : '';
        const terminal = ['completed', 'failed', 'cancelled'];
        if (terminal.includes(callStatus)) {
          setStatus({ ok: true, message: `Call ended (${callStatus})` });
          setActiveCallUUID(null);
        }
      }
    });

    return () => unsubscribe();
  }, [activeCallUUID]);

  // Poll for call status + recording URL while call is active
  useEffect(() => {
    if (!activeCallUUID) {
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }

    pollRef.current = setInterval(async () => {
      try {
        const r = await fetch(`${API_BASE}/calls/${activeCallUUID}`);
        if (!r.ok) return;
        const json = await r.json();
        const call = json.data ?? json;

        // Check for recording URL
        const url =
          (typeof call.recording_url === 'string' && call.recording_url) ||
          (call.recording_url?.String && call.recording_url.Valid ? call.recording_url.String : null);
        if (url) {
          setRecordingURL(url);
        }

        // Stop polling when call ends
        const terminal = ['completed', 'failed', 'cancelled'];
        const callStatus = typeof call.status === 'string' ? call.status : '';
        if (terminal.includes(callStatus)) {
          clearInterval(pollRef.current!);
          pollRef.current = null;
          if (!url) {
            setStatus({ ok: true, message: `Call ended (${callStatus})` });
          }
          setActiveCallUUID(null);
        }
      } catch {
        // ignore transient errors
      }
    }, 2000);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [activeCallUUID]);

  async function handleCall() {
    setLoading(true);
    setStatus(null);
    setRecordingURL(null);
    sipClient.unlockAudio(); // unlock Web Audio on this user gesture
    try {
      const resp = await fetch(`${API_BASE}/test/inbound-call`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sip_username: sipUsername,
          from_number: fromNumber,
          from_name: fromName,
          record,
        }),
      });
      const json = await resp.json();
      if (!resp.ok || !json.success) {
        setStatus({ ok: false, message: json.error?.message || 'Failed to initiate call' });
      } else {
        setActiveCallUUID(json.data.call_uuid);
        const recMsg = json.data.record ? ' · Recording enabled' : '';
        setStatus({ ok: true, message: `Ringing ${sipUsername} — UUID: ${json.data.call_uuid}${recMsg}` });
      }
    } catch (err) {
      setStatus({ ok: false, message: String(err) });
    } finally {
      setLoading(false);
    }
  }

  async function handleCancel() {
    if (!activeCallUUID) return;
    try {
      await fetch(`${API_BASE}/calls/${activeCallUUID}`, { method: 'DELETE' });
      setStatus({ ok: true, message: 'Test call cancelled' });
      setActiveCallUUID(null);
    } catch {
      setStatus({ ok: false, message: 'Failed to cancel call' });
    }
  }

  return (
    <section className="test-cradle">
      <h2 className="test-cradle-title">Inbound Call Test Cradle</h2>
      <p className="test-cradle-desc">
        Triggers FreeSWITCH to call a registered SIP agent. The agent's extension will ring as a real
        inbound call. The far end uses echo — the agent hears their own audio back.
      </p>

      <div className="test-cradle-form">
        <label className="test-cradle-label">Target Agent</label>
        <select
          className="test-cradle-select"
          value={sipUsername}
          onChange={(e) => setSipUsername(e.target.value)}
        >
          {users.length === 0 && <option value="">No agents found</option>}
          {users.map((u) => {
            const sip = extractStr(u.sip_username);
            const name = extractStr(u.name) || extractStr(u.email) || sip;
            return (
              <option key={u.id} value={sip}>
                {name} ({sip})
              </option>
            );
          })}
        </select>

        <label className="test-cradle-label">Caller ID Number</label>
        <input
          className="test-cradle-input"
          type="tel"
          value={fromNumber}
          onChange={(e) => setFromNumber(e.target.value)}
          placeholder="+15550001234"
        />

        <label className="test-cradle-label">Caller ID Name</label>
        <input
          className="test-cradle-input"
          type="text"
          value={fromName}
          onChange={(e) => setFromName(e.target.value)}
          placeholder="Test Caller"
        />

        <label className="test-cradle-label test-cradle-label--checkbox">
          <input
            type="checkbox"
            checked={record}
            onChange={(e) => setRecord(e.target.checked)}
            className="test-cradle-checkbox"
          />
          Record call
        </label>
      </div>

      <div className="test-cradle-actions">
        <button
          className="btn-test-call"
          onClick={handleCall}
          disabled={loading || !sipUsername}
        >
          {loading ? 'Calling...' : 'Initiate Inbound Call'}
        </button>
        {activeCallUUID && (
          <button className="btn-test-cancel" onClick={handleCancel}>
            Cancel Call
          </button>
        )}
      </div>

      {status && (
        <div className={`test-cradle-status ${status.ok ? 'ok' : 'err'}`}>{status.message}</div>
      )}

      {recordingURL && (
        <div className="test-cradle-recording">
          <p className="test-cradle-recording-label">📼 Recording ready</p>
          <audio controls src={recordingURL} className="test-cradle-audio" />
          <a href={recordingURL} download className="test-cradle-recording-download">
            Download WAV
          </a>
        </div>
      )}
    </section>
  );
}
